import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaClock, FaDumbbell, FaPlay, FaPlus, FaCalendarPlus, FaSearch, FaFilter, FaTimes, FaEdit } from 'react-icons/fa';
import ScheduleModal from './ScheduleModal';
import SEO from '../SEO';
import { PAGE_SEO, buildBreadcrumbs } from '../../utils/seoConfig';
import './WorkoutsListPage.css';

const WorkoutsListPage = () => {
  const navigate = useNavigate();
  const [workouts, setWorkouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [scheduleTarget, setScheduleTarget] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedDifficulty, setSelectedDifficulty] = useState('');
  const [selectedTargetArea, setSelectedTargetArea] = useState('');
  const [selectedVisibility, setSelectedVisibility] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchWorkouts();
  }, []);

  const fetchWorkouts = async () => {
    try {
      const headers = {};
      const token = localStorage.getItem('token');
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const response = await fetch(`${process.env.REACT_APP_API_URL}/fitness/workouts`, { headers });
      if (!response.ok) throw new Error('Failed to fetch workouts');
      const data = await response.json();
      setWorkouts(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'lower-body': return '#10b981';
      case 'upper-body': return '#3b82f6';
      case 'full-body': return '#8b5cf6';
      case 'core': return '#f59e0b';
      default: return '#6b7280';
    }
  };

  const filterOptions = useMemo(() => ({
    categories: [...new Set(workouts.map(workout => workout.category).filter(Boolean))].sort(),
    difficulties: [...new Set(workouts.map(workout => workout.difficulty).filter(Boolean))].sort(),
    targetAreas: [...new Set(workouts.flatMap(workout => workout.targetAreas || []).filter(Boolean))].sort()
  }), [workouts]);

  const normalizedSearchTerm = searchTerm.trim().toLowerCase();

  const filteredWorkouts = useMemo(() => workouts.filter((workout) => {
    const matchesSearch = !normalizedSearchTerm
      || workout.name?.toLowerCase().includes(normalizedSearchTerm)
      || workout.description?.toLowerCase().includes(normalizedSearchTerm)
      || workout.goal?.toLowerCase().includes(normalizedSearchTerm)
      || workout.targetAreas?.some(area => area.toLowerCase().includes(normalizedSearchTerm));

    const matchesCategory = !selectedCategory || workout.category === selectedCategory;
    const matchesDifficulty = !selectedDifficulty || workout.difficulty === selectedDifficulty;
    const matchesTargetArea = !selectedTargetArea || workout.targetAreas?.includes(selectedTargetArea);
    const matchesVisibility =
      !selectedVisibility
      || (selectedVisibility === 'coach'
        ? Boolean(workout.fromCoach)
        : selectedVisibility === 'mine'
          ? Boolean(workout.isOwn)
          : selectedVisibility === 'library'
            ? !workout.fromCoach
            : false);

    return matchesSearch && matchesCategory && matchesDifficulty && matchesTargetArea && matchesVisibility;
  }), [normalizedSearchTerm, selectedCategory, selectedDifficulty, selectedTargetArea, selectedVisibility, workouts]);

  const hasActiveFilters = Boolean(
    normalizedSearchTerm || selectedCategory || selectedDifficulty || selectedTargetArea || selectedVisibility
  );

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedCategory('');
    setSelectedDifficulty('');
    setSelectedTargetArea('');
    setSelectedVisibility('');
  };

  if (loading) {
    return (
      <div className="workouts-list-page">
        <div className="workouts-loading">
          <div className="loading-spinner" />
          <p>Loading workouts...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="workouts-list-page">
        <div className="workouts-error">
          <p>Error loading workouts: {error}</p>
          <button onClick={fetchWorkouts}>Try Again</button>
        </div>
      </div>
    );
  }

  const user = JSON.parse(localStorage.getItem('user') || 'null');

  const seo = PAGE_SEO['/fitness/workouts'];

  return (
    <div className="workouts-list-page">
      <SEO
        title={seo.title}
        description={seo.description}
        canonical="/fitness/workouts"
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: seo.title,
            description: seo.description,
            url: 'https://freshfitlab.io/fitness/workouts',
          },
          buildBreadcrumbs([
            { name: 'Home', url: '/' },
            { name: 'Fitness', url: '/fitness' },
            { name: 'Workouts', url: '/fitness/workouts' },
          ]),
        ]}
      />
      <div className="workouts-hero">
        <h1>All Workouts</h1>
        <p>Browse and start any workout</p>
        {user && (
          <button className="create-btn" onClick={() => navigate('/fitness/workout/create')}>
            <FaPlus /> Create Workout
          </button>
        )}
      </div>

      <div className="workouts-container">
        <div className="workouts-search-filter-section">
          <div className="workouts-search-box">
            <FaSearch className="workouts-search-icon" />
            <input
              type="text"
              placeholder="Search workouts..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button className="workouts-clear-search" onClick={() => setSearchTerm('')}>
                <FaTimes />
              </button>
            )}
          </div>

          <button
            className={`workouts-filter-toggle ${showFilters ? 'workouts-filter-active' : ''}`}
            onClick={() => setShowFilters(!showFilters)}
          >
            <FaFilter />
            <span>Filters</span>
            {hasActiveFilters && <span className="workouts-filter-badge" />}
          </button>
        </div>

        {showFilters && (
          <div className="workouts-filters-panel">
            <div className="workouts-filter-group">
              <label>Category</label>
              <select value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)}>
                <option value="">All Categories</option>
                {filterOptions.categories.map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
            </div>

            <div className="workouts-filter-group">
              <label>Difficulty</label>
              <select value={selectedDifficulty} onChange={(e) => setSelectedDifficulty(e.target.value)}>
                <option value="">All Levels</option>
                {filterOptions.difficulties.map(difficulty => (
                  <option key={difficulty} value={difficulty}>{difficulty}</option>
                ))}
              </select>
            </div>

            <div className="workouts-filter-group">
              <label>Target Area</label>
              <select value={selectedTargetArea} onChange={(e) => setSelectedTargetArea(e.target.value)}>
                <option value="">All Target Areas</option>
                {filterOptions.targetAreas.map(area => (
                  <option key={area} value={area}>{area}</option>
                ))}
              </select>
            </div>

            <div className="workouts-filter-group">
              <label>Source</label>
              <select value={selectedVisibility} onChange={(e) => setSelectedVisibility(e.target.value)}>
                <option value="">All workouts</option>
                <option value="mine">Created by me</option>
                <option value="library">Library workouts</option>
                <option value="coach">Coach workouts</option>
              </select>
            </div>

            {hasActiveFilters && (
              <button className="workouts-clear-filters" onClick={clearFilters}>
                Clear All Filters
              </button>
            )}
          </div>
        )}

        <div className="workouts-results-count">
          Showing {filteredWorkouts.length} of {workouts.length} workout{workouts.length !== 1 ? 's' : ''}
        </div>

        <div className="workouts-grid">
          {filteredWorkouts.map((workout) => (
            <div
              key={workout.workoutId}
              className="workout-card"
              onClick={() => navigate(`/fitness/workout/${workout.workoutId}`)}
            >
              <div 
                className="workout-card-indicator"
                style={{ backgroundColor: getCategoryColor(workout.category) }}
              />
              
              <div className="workout-card-body">
                <span className="workout-category">{workout.category}</span>
                {workout.isOwn && (
                  <span className="own-badge">My Workout</span>
                )}
                {workout.fromCoach && (
                  <span className="coach-badge"><i className="fas fa-user-tie"></i> {workout.coachName || 'Your Coach'}</span>
                )}
                <h3>{workout.name}</h3>
                <p className="workout-description">{workout.description}</p>

                <div className="workout-meta">
                  <div className="meta-item">
                    <FaClock />
                    <span>{workout.estimatedDuration} min</span>
                  </div>
                  <div className="meta-item">
                    <FaDumbbell />
                    <span>{workout.totalExercises} exercises</span>
                  </div>
                </div>

                <div className="workout-targets">
                  {workout.targetAreas?.slice(0, 3).map((area, idx) => (
                    <span key={idx} className="target-tag">{area}</span>
                  ))}
                </div>
              </div>

              <div className="workout-card-footer">
                <button 
                  className="start-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/fitness/active/${workout.workoutId}`);
                  }}
                >
                  <FaPlay />
                  <span>Start</span>
                </button>
                {user && workout.isOwn && workout._id && (
                  <button
                    type="button"
                    className="edit-workout-card-btn"
                    title="Edit workout"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/fitness/workout/${workout._id}/edit`);
                    }}
                  >
                    <FaEdit />
                  </button>
                )}
                {user && (
                  <button
                    className="schedule-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      setScheduleTarget(workout);
                    }}
                  >
                    <FaCalendarPlus />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        {filteredWorkouts.length === 0 && (
          <div className="no-workouts">
            <p>{workouts.length === 0 ? 'No workouts available yet.' : 'No workouts match your search or filters.'}</p>
            {hasActiveFilters && (
              <button className="workouts-empty-reset" onClick={clearFilters}>Clear Filters</button>
            )}
          </div>
        )}
      </div>

      {scheduleTarget && (
        <ScheduleModal
          type="workout"
          itemId={scheduleTarget.workoutId}
          itemName={scheduleTarget.name}
          onClose={() => setScheduleTarget(null)}
        />
      )}
    </div>
  );
};

export default WorkoutsListPage;

